import { useEffect, useMemo, useState } from "react";

const SCORE_URL = (eventId) =>
  `https://cache.tresting.com/v2/api/getScoreByEventId?eventId=${eventId}`;

const SCOREWIDGET_TOKEN =
  import.meta.env.VITE_SCOREWIDGET_TOKEN || "0d860d2d03f4726091f7d2f3ab9b22021164ed5e";

const scoreWidgetSrc = (matchId) =>
  `https://scorewidget.org/score/${SCOREWIDGET_TOKEN}/${matchId}`;

const asArr = (v) => {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (typeof v === "string") {
    return v
      .split(/[\s,|/]+/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
};

const ballLabel = (b) => {
  if (b == null || b === "") return "-";
  if (typeof b === "object") {
    return String(b.runs ?? b.run ?? b.ball ?? b.value ?? b.result ?? "-");
  }
  return String(b);
};

const ballTone = (raw) => {
  const v = ballLabel(raw).toUpperCase();
  if (v === "W" || v === "WK" || v.includes("OUT")) return "w";
  if (v === "6") return "six";
  if (v === "4") return "four";
  if (v === "WD" || v === "NB" || v === "B" || v === "LB") return "extra";
  if (v === "0" || v === "." || v === "-") return "dot";
  return "run";
};

/** Normalize provider payload — field names vary by feed. */
export function normalizeLiveScore(payload) {
  const d = payload?.data && typeof payload.data === "object" ? payload.data : payload;
  if (!d || typeof d !== "object") return null;

  const team1 = d.team1 || {};
  const team2 = d.team2 || {};

  const batsmen = asArr(d.batsman || d.batsmen || d.batsmanList).map((b) => ({
    name: b?.name || b?.playerName || b?.batsmanName || "—",
    run: b?.run ?? b?.runs ?? 0,
    ball: b?.ball ?? b?.balls ?? 0,
    fours: b?.fours ?? b?.["4s"] ?? 0,
    sixes: b?.sixes ?? b?.["6s"] ?? 0,
    sr: b?.strike_rate ?? b?.sr ?? b?.strikeRate ?? "—",
    onStrike: Boolean(b?.onStrike || b?.strike || b?.isOnStrike || b?.active),
  }));

  let bowler = d.bowler || d.currentBowler || null;
  if (!bowler && Array.isArray(d.bowlers) && d.bowlers.length) {
    bowler = d.bowlers.find((x) => x?.active || x?.isBowling) || d.bowlers[0];
  }
  if (typeof bowler === "string") {
    bowler = { name: bowler };
  }

  let currentOver = asArr(
    d.lastBalls || d.thisOver || d.currentOver || d.currentOverBalls || d.balls
  ).map(ballLabel);
  let previousOver = asArr(
    d.previousOver ||
      d.prevOver ||
      d.lastOver ||
      d.lastOverBalls ||
      d.secondLastBalls ||
      d.prevOverBalls
  ).map(ballLabel);

  if (!previousOver.length && currentOver.length > 6) {
    previousOver = currentOver.slice(-12, -6);
    currentOver = currentOver.slice(-6);
  }
  if (currentOver.length > 6) currentOver = currentOver.slice(-6);
  if (previousOver.length > 6) previousOver = previousOver.slice(-6);

  const statusText =
    d.currentBall ||
    d.spn ||
    d.commentary ||
    d.ballStatus ||
    d.statusMessage ||
    d.matchStatus ||
    "";

  const targetText = d.needByBall || d.target || d.required || d.result || "";

  return {
    team1: {
      name: team1.shortName || team1.name || d.team1Name || "T1",
      flag: team1.flag || "",
      score: team1.score || d.team1Score || "0-0",
      overs: team1.overs ?? d.team1Overs ?? "0",
    },
    team2: {
      name: team2.shortName || team2.name || d.team2Name || "T2",
      flag: team2.flag || "",
      score: team2.score || d.team2Score || "0-0",
      overs: team2.overs ?? d.team2Overs ?? "0",
    },
    batsmen,
    bowler: bowler
      ? {
          name: bowler.name || bowler.bowlerName || bowler.playerName || "—",
          overs: bowler.overs ?? bowler.over ?? "—",
          runs: bowler.runs ?? bowler.run ?? "—",
          wickets: bowler.wickets ?? bowler.wicket ?? bowler.w ?? "—",
          economy: bowler.economy ?? bowler.eco ?? bowler.er ?? "—",
        }
      : null,
    currentOver,
    previousOver,
    statusText: String(statusText),
    targetText: String(targetText),
    crr: d.crr ?? d.CRR ?? d.currentRunRate ?? null,
    rrr: d.rrr ?? d.RRR ?? d.requiredRunRate ?? null,
    lastBall: currentOver.length
      ? currentOver[currentOver.length - 1]
      : d.lastBall || d.recentBall || null,
  };
}

const BallChip = ({ value }) => (
  <span className={`vivo-ball vivo-ball--${ballTone(value)}`} title={ballLabel(value)}>
    {ballLabel(value)}
  </span>
);

const OverRow = ({ label, balls }) => (
  <div className="vivo-over-row">
    <span className="vivo-over-label">{label}</span>
    <div className="vivo-over-balls">
      {balls.length === 0
        ? Array.from({ length: 6 }).map((_, i) => (
            <span key={i} className="vivo-ball vivo-ball--empty">
              –
            </span>
          ))
        : balls.map((b, i) => <BallChip key={`${label}-${i}-${b}`} value={b} />)}
    </div>
  </div>
);

const ClientLiveScore = ({ matchId }) => {
  const [raw, setRaw] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [iframeOk, setIframeOk] = useState(true);

  useEffect(() => {
    if (!matchId) return undefined;
    let cancelled = false;

    const load = async () => {
      try {
        const res = await fetch(SCORE_URL(matchId));
        if (!res.ok) throw new Error(String(res.status));
        const json = await res.json();
        if (!cancelled) setRaw(json);
      } catch {
        /* iframe is primary */
      }
    };

    load();
    const t = setInterval(load, 4000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [matchId]);

  const score = useMemo(() => normalizeLiveScore(raw), [raw]);

  if (!matchId) return null;

  return (
    <section className="vivo-score vivo-score--widget" aria-label="Live scorecard">
      {iframeOk ? (
        <div className="vivo-score-iframe-wrap">
          <iframe
            title="Live score"
            src={scoreWidgetSrc(matchId)}
            className="vivo-score-iframe"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            onError={() => setIframeOk(false)}
          />
        </div>
      ) : null}

      {score ? (
        <>
          <button
            type="button"
            className="vivo-score-toggle"
            onClick={() => setShowDetails((v) => !v)}>
            {showDetails ? "Hide card score" : "Show card score"}
          </button>
          {showDetails ? (
            <div className="vivo-score-card-body">
              <div className="vivo-score-status is-live">
                <span className="vivo-score-pulse" aria-hidden="true" />
                <div className="vivo-score-status-text">
                  {score.statusText || "Live"}
                  {score.lastBall != null && score.lastBall !== "-" ? (
                    <em>
                      Last ball <b>{ballLabel(score.lastBall)}</b>
                    </em>
                  ) : null}
                </div>
              </div>
              {score.targetText ? <div className="vivo-score-target">{score.targetText}</div> : null}
              <div className="vivo-score-teams">
                {[score.team1, score.team2].map((t) => (
                  <div key={t.name} className="vivo-score-team">
                    <div className="vivo-score-team-meta">
                      {t.flag ? (
                        <img src={t.flag} alt="" />
                      ) : (
                        <span className="vivo-score-flag">{t.name?.[0]}</span>
                      )}
                      <strong>{t.name}</strong>
                    </div>
                    <div className="vivo-score-digits">
                      <b>{t.score}</b>
                      <small>({t.overs} ov)</small>
                    </div>
                  </div>
                ))}
              </div>
              <div className="vivo-score-overs">
                <OverRow label="This over" balls={score.currentOver} />
                <OverRow label="Last over" balls={score.previousOver} />
              </div>
              <div className="vivo-score-players">
                <div className="vivo-score-col-head">
                  <span>Batsman</span>
                  <span>R</span>
                  <span>B</span>
                  <span>4s</span>
                  <span>6s</span>
                  <span>SR</span>
                </div>
                {(score.batsmen.length
                  ? score.batsmen
                  : [{ name: "—", run: "—", ball: "—", fours: "—", sixes: "—", sr: "—" }]
                ).map((b) => (
                  <div key={b.name} className={`vivo-score-col-row ${b.onStrike ? "on-strike" : ""}`}>
                    <span>
                      {b.onStrike ? "✦ " : ""}
                      {b.name}
                    </span>
                    <span>{b.run}</span>
                    <span>{b.ball}</span>
                    <span>{b.fours || "—"}</span>
                    <span>{b.sixes || "—"}</span>
                    <span>{b.sr}</span>
                  </div>
                ))}
                <div className="vivo-score-bowl">
                  <span className="vivo-score-bowl-label">Bowler</span>
                  <div className="vivo-score-bowl-body">
                    <strong>{score.bowler?.name || "—"}</strong>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </>
      ) : !iframeOk ? (
        <div className="vivo-score vivo-score--soft">
          <div className="vivo-score-empty">Live score loading…</div>
        </div>
      ) : null}
    </section>
  );
};

export default ClientLiveScore;
